import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";

import { printOverrideBatch } from "../../api/batches";
import { setGlobalAlert } from "../../actions/common";
import { erpQuantityLabel, erpReasonLabel } from "../../helpers/erpUnresolved";
import "./OrderHeldScreen.css";

/**
 * Red station screen for a scanned ERP work id whose order is held with
 * `heldReason: "needs_labels"`. `held` is the `POST /batches/floor/scan`
 * payload: `{ kind: "order_held", orderId, externalOrderId, workId, unresolved }`.
 */
const OrderHeldScreen = ({ held, scanned, onReleased }) => {
  const dispatch = useDispatch();
  const [busy, setBusy] = useState(false);
  const lines = Array.isArray(held?.unresolved) ? held.unresolved : [];
  const heading = held?.workId || held?.externalOrderId || scanned;

  const printReady = async () => {
    if (!held?.orderId || busy) return;
    setBusy(true);
    try {
      await printOverrideBatch(held.orderId, { allowPartial: true });
      dispatch(
        setGlobalAlert({
          type: "success",
          message: "Printing what is ready. The rest waits for labels.",
        })
      );
      onReleased?.();
    } catch (error) {
      dispatch(
        setGlobalAlert({
          type: "error",
          message:
            error?.response?.data?.message ||
            error?.message ||
            "Could not release the ready lines.",
        })
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-content">
      <div className="ohs" role="alert">
        <div className="ohs__badge">Needs labels</div>
        <h1 className="ohs__title">
          Work Id <span className="ohs__mono">{heading}</span>
        </h1>
        <p className="ohs__sub">
          {held?.externalOrderId && held.externalOrderId !== heading
            ? `Order ${held.externalOrderId}. `
            : ""}
          {lines.length} line{lines.length === 1 ? "" : "s"} on this order{" "}
          {lines.length === 1 ? "has" : "have"} no label yet. Map the items on
          the Needs labels page, or print the lines that are ready now.
        </p>

        <table className="ohs__table">
          <thead>
            <tr>
              <th>Item Id</th>
              <th>Description</th>
              <th>Quantity</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => (
              <tr key={`${line.externalItemId || "line"}-${index}`}>
                <td className="ohs__mono">{line.externalItemId || "—"}</td>
                <td>{line.description || "—"}</td>
                <td className="ohs__mono">{erpQuantityLabel(line) || "—"}</td>
                <td>{erpReasonLabel(line.reason)}</td>
              </tr>
            ))}
            {!lines.length && (
              <tr>
                <td colSpan={4}>Waiting on labels for this order.</td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="ohs__actions">
          <button
            type="button"
            className="ohs__btn ohs__btn--primary"
            onClick={printReady}
            disabled={busy || !held?.orderId}
          >
            {busy ? "Releasing…" : "Print what's ready"}
          </button>
          <Link className="ohs__btn" to="/production/needs-labels">
            Open Needs labels
          </Link>
          {held?.orderId && (
            <Link
              className="ohs__link"
              to={`/orders/${encodeURIComponent(held.orderId)}`}
            >
              View order
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderHeldScreen;
